import { useState, useEffect, useCallback, memo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTask } from '@/contexts/TaskContext'
import { useWorkspace } from '@/contexts/WorkspaceContext'
import { useAppSelector, useAppDispatch } from '@/store/hooks'
import { startListening, stopListening } from '@/store/comments/commentsSlice'
import { startAgentStatusListening, stopAgentStatusListening } from '@/store/agentStatus/agentStatusSlice'
import { selectCommentsByTaskId } from '@/store/comments/commentsSelectors'
import { selectIsWorkspaceNotBoilerplate } from '@/store/workspace/workspaceSelectors'
import {
    deleteCommentSaga,
    type Comment
} from '@/store/comments/commentsSlice'
import { openConfirmDialog } from '@/store/ui/uiSlice'
import { PLATE_BOT_NAME, PLATE_BOT_AVATAR, AGENT_NAME } from '@/const/constants'
import Avatar from '@/components/ui/Avatar/Avatar'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '@/components/ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import IconOptions from '@/assets/icons/icon-options.svg?react'
import IconComments from '@/assets/icons/img-comments.svg?react'
import TaskModalCommentsAddComment from './components/TaskModalCommentsAddComment/TaskModalCommentsAddComment'
import TextStatic from '@/components/app/TextStatic/TextStatic'
import css from './TaskModalComments.module.css'
import { TextH3, TextMidd, TextNorm, TextNormBold } from '@/components/ui/Text'
import { useRelativeTime } from '@/hooks/useRelativeTime'
import { getMemberDisplayName } from '@/utils/memberDisplayName'

// A not-yet-resolved serverTimestamp reads as null; treat it as "just now" so a fresh
// comment sorts to the bottom instead of jumping to the top.
const getTimestamp = (t: any): number => t?.seconds ?? Number.MAX_SAFE_INTEGER

type Member = ReturnType<ReturnType<typeof useWorkspace>['getMember']>

interface CommentRowProps {
    comment: Comment
    member: Member | null
    isApiComment: boolean
    displayName: string
    canDelete: boolean
    timeAgo: string
    isDropdownOpen: boolean
    onToggleDropdown: (id: string) => void
    onDelete: (id: string) => void
}

// Memoized so a snapshot update or an unrelated parent re-render (e.g. opening another
// comment's menu) doesn't re-render every row — each row renders a static rich-text view,
// so a whole-thread re-render is wasteful on large threads. Comparator keys on id + updatedAt
// (comment objects get new refs on each comments snapshot even when unchanged).
const CommentRow = memo(({ comment, member, isApiComment, displayName, canDelete, timeAgo, isDropdownOpen, onToggleDropdown, onDelete }: CommentRowProps) => (
    <div className={css.comment} data-testid="comment-item">
        <div className={css.comment_avatar}>
            <Avatar
                title={displayName}
                color={isApiComment ? '#6366f1' : (member?.color || '#888')}
                avatar={isApiComment ? PLATE_BOT_AVATAR : member?.avatarImage}
                size="medium2"
            />
        </div>
        <div className={css.comment_content}>
            <div className={css.comment_header}>
                <div className={css.user_name}><TextNormBold>{displayName}</TextNormBold></div>
                <span className={css.time_ago}><TextNorm>{timeAgo}</TextNorm></span>
                {canDelete && (
                    <div className={css.comment_options}>
                        <DropdownWrapper>
                            <div onClick={() => onToggleDropdown(comment.id)} data-testid="comment-options-btn">
                                <IconOptions />
                            </div>
                            <Dropdown isOpen={isDropdownOpen} onClose={() => onToggleDropdown(comment.id)} align="right">
                                <DropdownItem title="Delete" danger={true} onClick={() => onDelete(comment.id)} testId="comment-delete-btn" />
                            </Dropdown>
                        </DropdownWrapper>
                    </div>
                )}
            </div>
            <div className={css.comment_text}><TextStatic value={comment.content} /></div>
        </div>
    </div>
), (prev, next) =>
    prev.comment.id === next.comment.id &&
    ((prev.comment.updatedAt as any)?.seconds ?? 0) === ((next.comment.updatedAt as any)?.seconds ?? 0) &&
    prev.isApiComment === next.isApiComment &&
    prev.displayName === next.displayName &&
    prev.canDelete === next.canDelete &&
    prev.timeAgo === next.timeAgo &&
    prev.isDropdownOpen === next.isDropdownOpen &&
    prev.member === next.member &&
    prev.onToggleDropdown === next.onToggleDropdown &&
    prev.onDelete === next.onDelete
)
CommentRow.displayName = 'CommentRow'

const TaskModalComments = () => {
    const dispatch = useAppDispatch()
    const navigate = useNavigate()
    const { task } = useTask()
    const { workspace, getMember } = useWorkspace()
    const currentUserId = useAppSelector(state => state.auth?.id)

    // Comments load per task, only while it's open.
    const workspaceId = workspace?.id
    const taskId = task?.id
    useEffect(() => {
        if (!taskId || !workspaceId) return
        dispatch(startListening({ taskId, workspaceId }))
        return () => { dispatch(stopListening()) }
    }, [taskId, workspaceId, dispatch])

    // "agent is working" flag for this task → shows a thinking indicator in the activity.
    useEffect(() => {
        if (!taskId || !workspaceId) return
        dispatch(startAgentStatusListening({ taskId, workspaceId }))
        return () => { dispatch(stopAgentStatusListening()) }
    }, [taskId, workspaceId, dispatch])

    const isRealWorkspace = useAppSelector(selectIsWorkspaceNotBoilerplate)
    const comments = useAppSelector(state => selectCommentsByTaskId(state, task?.id ?? ''))
    const agentStatus = useAppSelector(state => state.agentStatus.status)
    // Show the indicator only for this task, and ignore a stale flag (a crashed run that
    // never cleared it) older than 3 minutes.
    const agentWorking = !!agentStatus && agentStatus.working && agentStatus.taskId === task?.id &&
        (!agentStatus.startedAt || Date.now() / 1000 - agentStatus.startedAt.seconds < 180)
    const formatRelativeTime = useRelativeTime()
    const [openDropdownId, setOpenDropdownId] = useState<string | null>(null)

    const currentMember = getMember(currentUserId ?? '')
    const isAdmin = currentMember?.role === 'owner'

    const sortedComments = [...comments].sort(
        (a: Comment, b: Comment) => getTimestamp(a.createdAt) - getTimestamp(b.createdAt)
    )

    const handleToggleDropdown = useCallback((id: string) => {
        setOpenDropdownId(prev => prev === id ? null : id)
    }, [])

    const handleDeleteComment = useCallback((commentId: string) => {
        setOpenDropdownId(null)
        dispatch(openConfirmDialog({
            title: 'Delete comment',
            text: 'Are you sure you want to delete this comment? This cannot be undone.',
            confirmButtonTitle: 'Delete',
            confirmButtonAction: async () => {
                dispatch(deleteCommentSaga(commentId))
            },
        }))
    }, [dispatch])

    return (
        <div className={css.comments}>
            <div className={css.header}>
                <TextH3 className={css.comments_title}>Comments</TextH3>
            </div>
            {sortedComments.length > 0 && (
                <div className={css.list}>
                    {sortedComments.map((comment: Comment) => {
                        const isApiComment = !!comment.apiKeyName
                        const member = isApiComment ? null : getMember(comment.authorId ?? '')

                        // Never drop a comment whose author isn't currently resolvable (e.g. members
                        // still loading, or a former member) — render it with a fallback name instead.
                        const displayName = isApiComment
                            ? PLATE_BOT_NAME
                            : (member ? getMemberDisplayName(member) : 'Unknown')
                        const canDelete = isAdmin || comment.authorId === currentUserId

                        return (
                            <CommentRow
                                key={comment.id}
                                comment={comment}
                                member={member}
                                isApiComment={isApiComment}
                                displayName={displayName}
                                canDelete={canDelete}
                                timeAgo={formatRelativeTime(comment.createdAt)}
                                isDropdownOpen={openDropdownId === comment.id}
                                onToggleDropdown={handleToggleDropdown}
                                onDelete={handleDeleteComment}
                            />
                        )
                    })}
                </div>
            )}
            {agentWorking && (
                <div className={css.agent_working}>
                    <Avatar size="medium2" title={AGENT_NAME} color="#6366f1" avatar={PLATE_BOT_AVATAR} />
                    <TextMidd className={css.agent_working_text}>
                        {AGENT_NAME} is thinking<span className={css.dots} />
                    </TextMidd>
                </div>
            )}
            {isRealWorkspace ? (
                <TaskModalCommentsAddComment />
            ) : (
                <div
                    className={css.sign_up_placeholder}
                    onClick={() => navigate('/auth')}
                >
                    <IconComments />
                    <TextMidd className={css.sign_up_text}>Sign up to add comments</TextMidd>
                </div>
            )}
        </div>
    )
}

export default TaskModalComments
